<!-- 3D Model Viewer Component -->
<div class="model-viewer-container" [style.width]="width" [style.height]="height">

    <!-- Control Panel -->
    <div class="control-panel" *ngIf="showControls">
        <div class="control-group">
            <mat-button-toggle-group [(value)]="viewMode" class="view-mode-toggle">
                <mat-button-toggle value="rotate">
                    <mat-icon>3d_rotation</mat-icon>
                    <span class="toggle-label">{{ getTranslation('SHARED.3D_VIEWER.ROTATE_MODE', 'Rotate') }}</span>
                </mat-button-toggle>
                <mat-button-toggle value="select" *ngIf="allowSelection">
                    <mat-icon>touch_app</mat-icon>
                    <span class="toggle-label">{{ getTranslation('SHARED.3D_VIEWER.SELECT_MODE', 'Select') }}</span>
                </mat-button-toggle>
                <mat-button-toggle value="edit">
                    <mat-icon>edit</mat-icon>
                    <span class="toggle-label">{{ getTranslation('SHARED.3D_VIEWER.EDIT_MODE', 'Edit') }}</span>
                </mat-button-toggle>
            </mat-button-toggle-group>
        </div>

        <div class="control-group">
            <button mat-stroked-button (click)="resetView()" class="control-button">
                <mat-icon>refresh</mat-icon>
                <span>{{ getTranslation('SHARED.3D_VIEWER.RESET_VIEW', 'Reset View') }}</span>
            </button>

            <button mat-stroked-button (click)="toggleGrid()" class="control-button" [class.active]="showGrid">
                <mat-icon>grid_on</mat-icon>
                <span>{{ getTranslation('SHARED.3D_VIEWER.TOGGLE_GRID', 'Grid') }}</span>
            </button>

            <button mat-stroked-button (click)="toggleAxes()" class="control-button" [class.active]="showAxes">
                <mat-icon>straighten</mat-icon>
                <span>{{ getTranslation('SHARED.3D_VIEWER.TOGGLE_AXES', 'Axes') }}</span>
            </button>

            <button mat-stroked-button (click)="toggleWireframe()" class="control-button"
                [class.active]="wireframeMode">
                <mat-icon>crop_free</mat-icon>
                <span>{{ getTranslation('SHARED.3D_VIEWER.WIREFRAME', 'Wireframe') }}</span>
            </button>
        </div>

        <!-- Preset Views -->
        <div class="preset-views">
            <button mat-stroked-button (click)="setTopView()" class="preset-button">
                {{ getTranslation('SHARED.3D_VIEWER.TOP_VIEW', 'Top') }}
            </button>
            <button mat-stroked-button (click)="setFrontView()" class="preset-button">
                {{ getTranslation('SHARED.3D_VIEWER.FRONT_VIEW', 'Front') }}
            </button>
            <button mat-stroked-button (click)="setSideView()" class="preset-button">
                {{ getTranslation('SHARED.3D_VIEWER.SIDE_VIEW', 'Side') }}
            </button>
            <button mat-stroked-button (click)="setIsometricView()" class="preset-button">
                {{ getTranslation('SHARED.3D_VIEWER.ISOMETRIC_VIEW', 'Isometric') }}
            </button>
        </div>
    </div>

    <!-- 3D Viewport -->
    <div class="model-3d-container">
        <!-- Instructions -->
        <div class="instructions" *ngIf="viewMode === 'rotate'">
            <p>{{ getTranslation('SHARED.3D_VIEWER.ROTATE_INSTRUCTIONS', 'Drag to rotate the model, scroll to zoom
                in/out') }}</p>
        </div>
        <div class="instructions" *ngIf="viewMode === 'select'">
            <p>{{ getTranslation('SHARED.3D_VIEWER.SELECT_INSTRUCTIONS', 'Click on objects to select and view details')
                }}</p>
        </div>
        <div class="instructions" *ngIf="viewMode === 'edit'">
            <p>{{ getTranslation('SHARED.3D_VIEWER.EDIT_INSTRUCTIONS', 'Click and drag to modify object positions') }}
            </p>
        </div>

        <!-- 3D Scene Viewport -->
        <div class="model-3d-viewport" [class.selecting]="viewMode === 'select'"
            [class.rotating]="viewMode === 'rotate'" [class.editing]="viewMode === 'edit'" [class.show-grid]="showGrid"
            [class.wireframe]="wireframeMode" (mousedown)="onMouseDown($event)" (mousemove)="onMouseMove($event)"
            (mouseup)="onMouseUp()" (wheel)="onWheel($event)">
            <!-- Grid Background -->
            <div class="grid-background" *ngIf="showGrid">
                <div class="grid-lines-x"></div>
                <div class="grid-lines-z"></div>
            </div>

            <!-- Coordinate Axes -->
            <div class="coordinate-axes" *ngIf="showAxes">
                <div class="axis axis-x"></div>
                <div class="axis axis-y"></div>
                <div class="axis axis-z"></div>
            </div>

            <!-- 3D Scene -->
            <div #viewer3d class="scene-3d" [style.transform]="getSceneTransformStyle()" [class.dragging]="isDragging">
                <!-- 3D Objects -->
                <div *ngFor="let object of scene.objects; trackBy: trackObject" class="object-3d"
                    [class]="'object-type-' + object.type" [class.selected]="object.isSelected"
                    [class.interactive]="allowSelection && viewMode === 'select'"
                    [style.transform]="getObjectTransformStyle(object)" [ngStyle]="getObjectColorStyle(object)"
                    (click)="onObjectClick(object)" [attr.title]="object.name">
                    <!-- Object Content -->
                    <div class="object-content">
                        <div class="object-face face-front"></div>
                        <div class="object-face face-back"></div>
                        <div class="object-face face-left"></div>
                        <div class="object-face face-right"></div>
                        <div class="object-face face-top"></div>
                        <div class="object-face face-bottom"></div>
                    </div>

                    <!-- Object Label -->
                    <div class="object-label" *ngIf="showLabels && object.name">
                        <span>{{ object.name }}</span>
                        <div class="label-details" *ngIf="object.metadata">
                            <small *ngFor="let key of object.metadata | keyvalue">
                                {{ key.key }}: {{ key.value }}
                            </small>
                        </div>
                    </div>

                    <!-- Selection Indicator -->
                    <div class="selection-indicator" *ngIf="object.isSelected">
                        <mat-icon>check_circle</mat-icon>
                    </div>
                </div>
            </div>

            <!-- Loading Indicator -->
            <div class="loading-indicator" *ngIf="!scene.objects.length">
                <mat-spinner diameter="40"></mat-spinner>
                <p>{{ getTranslation('SHARED.3D_VIEWER.LOADING_MODEL', 'Loading 3D model...') }}</p>
            </div>
        </div>
    </div>

    <!-- Object Info Panel -->
    <div class="object-info-panel" *ngIf="selectedObject">
        <h4>{{ selectedObject.name }}</h4>
        <div class="object-properties">
            <div class="property">
                <span>{{ getTranslation('SHARED.3D_VIEWER.TYPE', 'Type') }}:</span>
                <span>{{ selectedObject.type }}</span>
            </div>
            <div class="property">
                <span>{{ getTranslation('SHARED.3D_VIEWER.POSITION', 'Position') }}:</span>
                <span>X: {{ selectedObject.position.x }}, Y: {{ selectedObject.position.y }}, Z: {{
                    selectedObject.position.z }}</span>
            </div>
            <div class="property" *ngIf="selectedObject.metadata">
                <span>{{ getTranslation('SHARED.3D_VIEWER.DETAILS', 'Details') }}:</span>
                <div class="metadata">
                    <div *ngFor="let item of selectedObject.metadata | keyvalue" class="metadata-item">
                        <strong>{{ item.key }}:</strong> {{ item.value }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>